import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewChildren,
	QueryList,
	NgZone,
	ChangeDetectorRef,
	HostListener,
	AfterViewInit
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import 'prismjs/components/prism-json';
import { AuthService } from '../../../services/auth.service';
import { ComponentService, CreatorFrontApi } from '../../../services/component.service';
import { OverlayService } from '../../../services/overlay.service';
import { Subscription } from 'rxjs';
import { filter, distinctUntilChanged, map, mergeMap } from 'rxjs/operators';
import { NewApiHttpService } from '../../../services/new-api-http.service';
import { MaintenanceService } from '../../../services/maintenance.service';

declare const Prism;

@Component({
	selector: 'app-preview',
	templateUrl: './preview.component.html',
	styleUrls: ['./preview.component.scss']
})
export class PreviewComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('snippet', { static: false }) snippet: ElementRef;
	@ViewChild('personalizedMultiImagePreviewCountdown', { static: false }) personalizedMultiImagePreviewCountdown: ElementRef;
	@ViewChildren('personalizedMultiImagePreview') personalizedMultiImagePreview: QueryList<ElementRef>;
	@ViewChildren('personalizedMultiImagePreviewCountdownOT') personalizedMultiImagePreviewCountdownOT: QueryList<ElementRef>;
	@ViewChildren('recommendationPreview') recommendationPreview: QueryList<ElementRef>;
	snippetHtml: string;
	previewResponsiveType = String('desktop');
	previewHeight: string;
	previewInnerHeight: string;
	dynamicStatic: Boolean = Boolean(true);
	componentData: any;
	componentDataSubscription: Subscription;
	previewContentSubscription: Subscription;
	overlaySubscription: Subscription;
	previewDataSubscription: Subscription;
	isAdmin;
	isConfigShown: Boolean = Boolean(false);
	isMultiTagPreviewShown: Boolean = Boolean(false);
	modalHeight = window.innerHeight * 0.9;
	innerHeight = this.modalHeight - 90;
	bodyHeight;
	designedSnippet;
	stringifyConfig;
	editMode: Boolean = Boolean(false);
	isHowtoShown: Boolean = Boolean(false);
	howtoURL;
	refresh: Boolean = Boolean(false);
	refreshType;
	refreshText;
	support = { apple: '', android: '', gmail: '', outlook: '', 'outlook.com': '', yahoo: '' };
	isCustomComponent: Boolean = Boolean(false);
	cachedLinkUrl = String();
	previewTagValue;
	apiPreviewHTML;
	previewTagList;
	editPage = false;
	regexIMG = /<img.*?src="(.*?)"/;
	regexCTA = /<a.*?href="(.*?)"/;
	customGuideForUI = false;
	recoMergeTagArray;
	copyButtonText = 'Copy to Clipboard';
	countdownPersonalizedUpdatePreview = false;

	@HostListener('window:resize', ['$event'])
	onResize(event) {
		this.previewHeight = String(`${window.innerHeight - 275}px`);
		this.previewInnerHeight = String(`${window.innerHeight - 296}px`);
	}

	constructor(
		private component: ComponentService,
		private newApiHttpService: NewApiHttpService,
		private http: HttpClient,
		private auth: AuthService,
		private overlay: OverlayService,
		private router: Router,
		private zone: NgZone,
		private ref: ChangeDetectorRef,
		private maintenance: MaintenanceService
	) {
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				map((event: NavigationEnd) => event.url)
			)
			.subscribe((url) => {
				this.editPage = url.includes('/edit/') || url.includes('/new/') || url.includes('/duplicate/');
			});
	}

	ngOnInit() {
		this.isAdmin = this.auth.isAdmin();
		this.previewHeight = String(`${window.innerHeight - 273}px`);
		this.previewInnerHeight = String(`${window.innerHeight - 294}px`);
		this.innerHeight = this.modalHeight == 0 ? 400 : this.innerHeight;
		this.modalHeight = this.modalHeight == 0 ? 490 : this.modalHeight;

		let suportedComponents$ = this.component.getSupportedComponents();

		this.componentDataSubscription = this.component.componentData
			.pipe(
				distinctUntilChanged(),
				mergeMap((componentResult) => {
					this.componentData = componentResult;

					if (this.componentData && this.componentData.config && this.componentData.config.type === 24) {
						this.isCustomComponent = true;
					}
					if (this.componentData && this.componentData.config && this.componentData.config.type === 50) {
						this.recoMergeTagArray = Object.entries(this.componentData.config.merge_tags);
					}

					return suportedComponents$;
				})
			)
			.subscribe((supportedComponentsResults) => {
				if (this.componentData) {
					const supportedcomponentItem = supportedComponentsResults.data.find((data) => {
						return data.type === this.componentData.config.type;
					});
					const emailSupportMatrix =
						supportedcomponentItem && supportedcomponentItem.email_support ? supportedcomponentItem.email_support : {};

					this.support.apple = emailSupportMatrix.Apple ? emailSupportMatrix.Apple : 'TBD';
					this.support.android = emailSupportMatrix.Android ? emailSupportMatrix.Android : 'TBD';
					this.support.gmail = emailSupportMatrix.Gmail ? emailSupportMatrix.Gmail : 'TBD';
					this.support.outlook = emailSupportMatrix.Outlook ? emailSupportMatrix.Outlook : 'TBD';
					this.support['outlook.com'] = emailSupportMatrix['Outlook.com'] ? emailSupportMatrix['Outlook.com'] : 'TBD';
					this.support.yahoo = emailSupportMatrix.Yahoo ? emailSupportMatrix.Yahoo : 'TBD';
				}
			});

		this.previewContentSubscription = this.component.previewContent.subscribe((result) => {
			this.snippetHtml =
				result ||
				`
              <div class='alert alert-info' role='alert'>
                  <small>No data to display...</small>
              </div>
          `;
			this.updateSnippet();
		});

		this.previewDataSubscription = this.component.previewData
			.pipe(filter((data) => this.componentData && this.componentData.config && this.componentData.config.type !== 46))
			.subscribe((data) => this.apiPreview());

		this.overlaySubscription = this.overlay.refreshData.subscribe((callback) => {
			this.zone.run(() => {
				this.refresh = !!callback.active;
				this.refreshText = callback.text;
				this.refreshType = callback.type;
				this.ref.markForCheck();
			});
		});
	}

	ngAfterViewInit(): void {
		this.updateSnippet();
	}

	ngOnDestroy() {
		this.designedSnippet = Prism.highlight(JSON.stringify({}, null, '\t'), Prism.languages.json);
		this.stringifyConfig = JSON.stringify({}, null, '\t');
		this.component.updateComponentData(null);
		this.componentDataSubscription.unsubscribe();
		this.overlaySubscription.unsubscribe();
		this.previewContentSubscription.unsubscribe();
		this.previewDataSubscription.unsubscribe();
	}

	updateSnippet(): void {
		if (this.snippet) {
			if (
				this.componentData &&
				this.componentData.config.type == 32 &&
				this.previewTagValue &&
				this.previewTagValue.tag != 'default'
			) {
				this.updateSegmentedPreview({ event: this.previewTagValue.tag, countdown: false });
			} else {
				if (
					this.componentData &&
					this.componentData.config.type == 7 &&
					this.componentData.config.web_version &&
					this.snippetHtml.includes('script src')
				) {
					this.webVersionCountdownPreview(this.snippetHtml);
				} else {
					this.snippet.nativeElement.innerHTML =
						this.snippetHtml || `<div class='alert alert-info' role='alert'><small>No data to display...</small></div>`;
				}
			}
		}
	}

	apiPreview() {
		this.component.updatePreviewPromiseStatus(false);
		this.overlay.updateRefresh(true, 'Updating...', 'refresh');

		if (this.componentData.config.type === 50) {
			this.recoMergeTagArray = Object.entries(this.componentData.config.merge_tags);
		}

		const data = {
			name: this.componentData.name,
			legacy: this.componentData.legacy,
			config: JSON.stringify(this.componentData.config)
		};

		this.newApiHttpService.newApiRequestPost('widget/preview', data).subscribe(
			(response: CreatorFrontApi) => {
				const respError =
					response && response.error
						? response.error
						: `Ugh, it looks like this component is not accessible.
                   Please contact dmsupport@optimove.com if you need help.`;

				if (response && response.status) {
					if (this.componentData.config.type == 54) {
						this.componentData.config = response.data.config;
					}
					this.overlay.updateRefresh(false);
					this.apiPreviewHTML = response.data.snippet;
					if (this.componentData.config.type == 32 && this.previewTagValue && this.previewTagValue.tag != 'default') {
						this.updateSegmentedPreview({ event: this.previewTagValue.tag, countdown: false });
					} else if (this.countdownPersonalizedUpdatePreview && this.componentData.config.type == 7) {
						this.countdownPersonalizedPreview(this.apiPreviewHTML);
					} else {
						if (this.snippet) {
							if (
								this.componentData.config.type == 7 &&
								this.componentData.config.web_version &&
								this.apiPreviewHTML.includes('script src')
							) {
								this.webVersionCountdownPreview(this.apiPreviewHTML);
							} else {
								this.snippet.nativeElement.innerHTML = this.apiPreviewHTML;
							}
						}
					}
				} else {
					this.overlay.updateRefresh(false);
					this.snippet.nativeElement.innerHTML = `
                        <div class='alert alert-info' role='alert'>
                            <small>${respError}</small>
                        </div>
                    `;
				}
				this.component.updatePreviewPromiseStatus(true);
				document.body.style.cursor = 'default';
			},
			(err) => {
				this.overlay.updateRefresh(false);
				this.snippet.nativeElement.innerHTML = `<div class='alert alert-info' role='alert'>${err}</div>`;
				this.component.updatePreviewPromiseStatus(true);
				document.body.style.cursor = 'default';
				this.overlay.updateOverlay(true, err, 'alert');
				this.maintenance.serverMaintenance(err);
			}
		);
	}

	showConfig() {
		this.designedSnippet =
			this.componentData && this.componentData.config
				? Prism.highlight(JSON.stringify(this.componentData.config, null, '\t'), Prism.languages.json)
				: Prism.highlight(JSON.stringify({}, null, '\t'), Prism.languages.json);

		this.stringifyConfig =
			this.componentData && this.componentData.config
				? JSON.stringify(this.componentData.config, null, '\t')
				: JSON.stringify({}, null, '\t');
	}

	editConfig() {
		this.componentData.config = JSON.parse(this.stringifyConfig);
		this.component.updateComponentData(this.componentData);
		this.component.updatePreviewData();
		this.designedSnippet = Prism.highlight(JSON.stringify(this.componentData.config, null, '\t'), Prism.languages.json);
		this.stringifyConfig = JSON.stringify(this.componentData.config, null, '\t');
	}

	toggleDynamic(params) {
		if (params) {
			this.dynamicStatic = !this.dynamicStatic;
		}
	}

	onPreviewScroll(event) {
		this.component.getTimerPopupPosition();
	}

	updateSegmentedPreview(params) {
		if (params.event != 'default') {
			if (params.dropdown) {
				this.previewTagValue = params.previewTagList.find((x) => x.tag === params.event);
			}
			let updatedPreviewHTML = this.snippetHtml.replace(this.regexIMG, `<img src="${this.previewTagValue.image}"`);

			if (this.componentData.config.link && this.previewTagValue.link) {
				updatedPreviewHTML = updatedPreviewHTML.replace(this.regexCTA, `<a href="${this.previewTagValue.link}"`);
			}
			this.snippet.nativeElement.innerHTML = updatedPreviewHTML;
		} else if (this.apiPreviewHTML) {
			this.snippet.nativeElement.innerHTML = this.apiPreviewHTML;
		} else {
			this.snippet.nativeElement.innerHTML = this.snippetHtml;
		}
	}

	getCSVData(url) {
		let csvData = [];
		this.http.get(url, { responseType: 'text' }).subscribe((data) => {
			let csvRows = data.split('\r\n');
			csvRows.forEach((item) => {
				if (item.length) {
					const data = item.split(',');
					csvData.push({
						tag: data[0],
						image: data[1],
						link: data[2] ? data[2] : ''
					});
				}
			});
			this.previewTagList = csvData.slice(1);
		});
	}

	countdownPreview(event) {
		let countPreviewSnippet = this.editPage && this.apiPreviewHTML ? this.apiPreviewHTML : this.snippetHtml;
		if (event == 'expired') {
			const regexFileName = /countdown.gif/;
			let updatedPreviewHTML = this.componentData.config.expiration_image
				? countPreviewSnippet.replace(this.regexIMG, `<img src="${this.componentData.config.expiration_image}"`)
				: countPreviewSnippet.replace(regexFileName, `expiration.png`);
			if (this.componentData.config.link && this.componentData.config.expiration_link) {
				updatedPreviewHTML = updatedPreviewHTML.replace(this.regexCTA, `<a href="${this.componentData.config.expiration_link}"`);
			}
			this.snippet.nativeElement.innerHTML = updatedPreviewHTML;
		} else if (event == 'iOSFallback') {
			let updatedPreviewHTML = countPreviewSnippet.replace(
				this.regexIMG,
				`<img src="${this.componentData.config.ios_privacy_fallback}"`
			);
			if (this.componentData.config.link && this.componentData.config.ios_privacy_fallback_link) {
				updatedPreviewHTML = updatedPreviewHTML.replace(
					this.regexCTA,
					`<a href="${this.componentData.config.ios_privacy_fallback_link}"`
				);
			}
			this.snippet.nativeElement.innerHTML = updatedPreviewHTML;
		} else if (event == 'default' && this.editPage) {
			document.body.style.cursor = 'progress';
			this.apiPreview();
		} else if (event == 'default' && !this.editPage) {
			this.snippet.nativeElement.innerHTML = this.snippetHtml;
		} else if (event == 'personalization') {
			this.countdownPersonalizedUpdatePreview = true;
			this.showPreviewMultiTagModal(true);
		} else if (event == 'fallback') {
			if (this.componentData.config.personalized_options.fallback_type == 'image') {
				const regexFileName = /countdown.gif/;
				// eslint-disable-next-line prettier/prettier, max-len
				let updatedPreviewHTML = this.componentData.config.personalized_options.fallback_image ? countPreviewSnippet.replace(this.regexIMG, `<img src="${this.componentData.config.personalized_options.fallback_image}"`) : countPreviewSnippet.replace(regexFileName, `fallback.png`);
				if (this.componentData.config.link && this.componentData.config.personalized_options.fallback_cta) {
					updatedPreviewHTML = updatedPreviewHTML.replace(
						this.regexCTA,
						`<a href="${this.componentData.config.personalized_options.fallback_cta}"`
					);
				}
				this.snippet.nativeElement.innerHTML = updatedPreviewHTML;
			} else {
				this.snippet.nativeElement.innerHTML = `<img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=">`;
			}
		}
	}

	updatePersonalizedImageTag(event) {
		const multiTagVersion = this.componentData.config.version > 1 && this.componentData.config.merge_array.length != 0;
		const tag = multiTagVersion
			? this.componentData.config.merge_array[0].merge_tag.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
			: this.componentData.config.merge_tag.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');

		if (event != '') {
			if (this.apiPreviewHTML && this.editPage) {
				this.snippet.nativeElement.innerHTML = this.apiPreviewHTML.replace(new RegExp(tag, 'g'), event);
			} else if ((this.snippetHtml && !this.editPage) || this.snippetHtml) {
				this.snippet.nativeElement.innerHTML = this.snippetHtml.replace(new RegExp(tag, 'g'), event);
			} else {
				this.overlay.updateOverlay(
					true,
					'Unable to update the preview with the entered tag. Please update using the "Update Preview" button then try to update the tag again.',
					'alert'
				);
			}
		}
	}

	updateMultiTagPreview() {
		let snippetMulti = '';
		if (this.apiPreviewHTML && this.editPage) {
			snippetMulti = this.apiPreviewHTML;
		} else if ((this.snippetHtml && !this.editPage) || this.snippetHtml) {
			snippetMulti = this.snippetHtml;
		} else {
			this.overlay.updateOverlay(
				true,
				'Unable to update the preview with the entered tag(s). Please try to update using the "Update Preview" button then preview the tag(s) again.',
				'alert'
			);
		}

		if (this.componentData.config.type === 7) {
			if (this.editPage) {
				this.apiPreview();
			} else {
				this.countdownPersonalizedPreview(snippetMulti);
			}
		} else {
			const formValuesArray =
				this.componentData.config.type === 23 ? this.personalizedMultiImagePreview.toArray() : this.recommendationPreview.toArray();

			for (const previewTag of formValuesArray) {
				if (this.componentData.config.type === 23) {
					const tagEntry = this.componentData.config.merge_array.find(
						(item) => item.tag_name == previewTag.nativeElement.id.replace('personalizedMultiImagePreview', '')
					);
					// @ts-ignore
					snippetMulti = snippetMulti.replaceAll(tagEntry.merge_tag, previewTag.nativeElement.value);
				} else if (this.componentData.config.type === 50) {
					const tagEntry = this.componentData.config.merge_tags[previewTag.nativeElement.id.replace('recommendationPreview', '')];
					// @ts-ignore
					snippetMulti = snippetMulti.replaceAll(tagEntry, previewTag.nativeElement.value);
				}
			}

			this.snippet.nativeElement.innerHTML = snippetMulti;
			this.isMultiTagPreviewShown = false;
		}
	}

	countdownPersonalizedPreview(snippet) {
		if (this.componentData.config.personalized_options.type == 'mergeTag') {
			snippet = snippet.replace(
				this.componentData.config.personalized_options.merge_tag,
				this.personalizedMultiImagePreviewCountdown.nativeElement.value
			);
		} else {
			const formFields = this.personalizedMultiImagePreviewCountdownOT.toArray();
			for (const field of formFields) {
				if (field.nativeElement.id === 'personalizedMultiImagePreviewCountdownOpen') {
					snippet = snippet.replace(this.componentData.config.personalized_options.time_from_open, field.nativeElement.value);
				} else if (field.nativeElement.id === 'personalizedMultiImagePreviewCountdownUID') {
					snippet = snippet.replace(this.componentData.config.dm_params.uid, field.nativeElement.value);
				} else if (field.nativeElement.id === 'personalizedMultiImagePreviewCountdownDPID') {
					snippet = snippet.replace(this.componentData.config.dm_params.dpid, field.nativeElement.value);
				}
			}
		}
		this.snippet.nativeElement.innerHTML = snippet;
		this.isMultiTagPreviewShown = false;
		this.countdownPersonalizedUpdatePreview = false;
	}

	updateProgressImageTag(event) {
		if (event != '') {
			if (this.apiPreviewHTML && this.editPage) {
				// @ts-ignore
				this.snippet.nativeElement.innerHTML = this.apiPreviewHTML.replaceAll(this.componentData.config.merge_tag, event);
			} else if ((this.snippetHtml && !this.editPage) || this.snippetHtml) {
				// @ts-ignore
				this.snippet.nativeElement.innerHTML = this.snippetHtml.replaceAll(this.componentData.config.merge_tag, event);
			} else {
				this.overlay.updateOverlay(true, 'Unable to update the preview with the entered tag.', 'alert');
			}
		}
	}

	showPreviewMultiTagModal(event) {
		if (event) {
			this.isMultiTagPreviewShown = true;
		}
	}

	processReturnedData(event) {
		this[event.method](event.data);
	}

	resetCopyText(originalText) {
		setTimeout(() => (this.copyButtonText = originalText), 3000);
	}

	showHowTo() {
		this.isHowtoShown = this.componentData ? true : false;
	}

	webVersionCountdownPreview(snippet) {
		this.snippet.nativeElement.innerHTML = '';

		let html = `<body style="padding:0;margin:0;">${snippet}</body>`;

		const iframe = document.createElement('iframe');
		iframe.style.width = `${this.componentData.config.base.background.width}px`;
		iframe.style.height = `${this.componentData.config.base.background.height}px`;
		iframe.setAttribute('scrolling', 'no');
		iframe.setAttribute('frameborder', '0');
		this.snippet.nativeElement.appendChild(iframe);

		iframe.contentWindow.document.open();
		iframe.contentWindow.document.write(html);
		iframe.contentWindow.document.close();
	}
}
